<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount, shallowRef } from 'vue'
import EasyMDE from 'easymde'
import { renderMarkdown } from '@/utils/renderMarkdown'

const props = withDefaults(
  defineProps<{
    modelValue: string
    placeholder?: string
    maxLength?: number
    disabled?: boolean
    inputId?: string
  }>(),
  {
    placeholder: '',
    maxLength: 50000,
    disabled: false,
    inputId: undefined,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const textareaRef = ref<HTMLTextAreaElement | null>(null)
const instance = shallowRef<EasyMDE | null>(null)

onMounted(() => {
  if (!textareaRef.value) return

  const easyMDE = new EasyMDE({
    element: textareaRef.value,
    initialValue: props.modelValue,
    placeholder: props.placeholder,
    spellChecker: false,
    status: false,
    autofocus: false,
    forceSync: true,
    autoDownloadFontAwesome: false,
    minHeight: '320px',
    previewRender: (plainText: string) => renderMarkdown(plainText),
    toolbar: [
      'heading-1',
      'heading-2',
      '|',
      'bold',
      'italic',
      '|',
      'unordered-list',
      'ordered-list',
      '|',
      'link',
      '|',
      'preview',
      'side-by-side',
      'fullscreen',
    ],
  })

  easyMDE.codemirror.on('change', () => {
    const value = easyMDE.value()
    if (value.length > props.maxLength) {
      const truncated = value.slice(0, props.maxLength)
      easyMDE.value(truncated)
      emit('update:modelValue', truncated)
    } else {
      emit('update:modelValue', value)
    }
  })

  if (props.disabled) {
    easyMDE.codemirror.setOption('readOnly', true)
  }

  instance.value = easyMDE
})

watch(
  () => props.modelValue,
  (newValue) => {
    if (!instance.value) return
    const current = instance.value.value()
    if (current !== newValue) {
      instance.value.value(newValue)
    }
  },
)

watch(
  () => props.disabled,
  (disabled) => {
    if (!instance.value) return
    instance.value.codemirror.setOption('readOnly', disabled)
  },
)

onBeforeUnmount(() => {
  if (instance.value) {
    instance.value.toTextArea()
    instance.value.cleanup()
    instance.value = null
  }
})
</script>

<template>
  <div class="markdown-editor-wrapper">
    <textarea
      :id="inputId"
      ref="textareaRef"
    />
  </div>
</template>

<style scoped>
.markdown-editor-wrapper :deep(.EasyMDEContainer) {
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid rgb(51 65 85);
}

.markdown-editor-wrapper :deep(.editor-toolbar) {
  background-color: rgb(30 41 59);
  border-color: rgb(51 65 85);
  border-bottom-color: rgb(51 65 85);
  padding: 6px 10px;
}

.markdown-editor-wrapper :deep(.editor-toolbar button) {
  color: rgb(148 163 184);
}

.markdown-editor-wrapper :deep(.editor-toolbar button:hover),
.markdown-editor-wrapper :deep(.editor-toolbar button.active) {
  background-color: rgb(51 65 85);
  color: rgb(255 255 255);
  border-color: rgb(71 85 105);
}

.markdown-editor-wrapper :deep(.editor-toolbar i.separator) {
  border-color: rgb(51 65 85);
}

.markdown-editor-wrapper :deep(.CodeMirror) {
  background-color: rgb(15 23 42);
  color: rgb(226 232 240);
  border: none;
  min-height: 320px;
  font-size: 14px;
  line-height: 1.6;
}

.markdown-editor-wrapper :deep(.CodeMirror-cursor) {
  border-left-color: rgb(52 211 153);
}

.markdown-editor-wrapper :deep(.CodeMirror-selected) {
  background-color: rgb(52 211 153 / 0.2) !important;
}

.markdown-editor-wrapper :deep(.editor-preview),
.markdown-editor-wrapper :deep(.editor-preview-side) {
  background-color: rgb(15 23 42);
  color: rgb(226 232 240);
  border-color: rgb(51 65 85);
  padding: 1rem 1.25rem;
}

.markdown-editor-wrapper :deep(.editor-preview p),
.markdown-editor-wrapper :deep(.editor-preview-side p) {
  margin-bottom: 1rem;
}

.markdown-editor-wrapper :deep(.editor-preview p:last-child),
.markdown-editor-wrapper :deep(.editor-preview-side p:last-child) {
  margin-bottom: 0;
}

.markdown-editor-wrapper :deep(.editor-preview h1),
.markdown-editor-wrapper :deep(.editor-preview h2),
.markdown-editor-wrapper :deep(.editor-preview h3),
.markdown-editor-wrapper :deep(.editor-preview-side h1),
.markdown-editor-wrapper :deep(.editor-preview-side h2),
.markdown-editor-wrapper :deep(.editor-preview-side h3) {
  color: rgb(255 255 255);
  font-weight: 700;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
}

.markdown-editor-wrapper :deep(.editor-preview h1),
.markdown-editor-wrapper :deep(.editor-preview-side h1) { font-size: 1.5rem; }

.markdown-editor-wrapper :deep(.editor-preview h2),
.markdown-editor-wrapper :deep(.editor-preview-side h2) { font-size: 1.25rem; }

.markdown-editor-wrapper :deep(.editor-preview h3),
.markdown-editor-wrapper :deep(.editor-preview-side h3) { font-size: 1.125rem; }

.markdown-editor-wrapper :deep(.editor-preview ul),
.markdown-editor-wrapper :deep(.editor-preview-side ul) {
  list-style-type: disc;
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}

.markdown-editor-wrapper :deep(.editor-preview ol),
.markdown-editor-wrapper :deep(.editor-preview-side ol) {
  list-style-type: decimal;
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}

.markdown-editor-wrapper :deep(.editor-preview li),
.markdown-editor-wrapper :deep(.editor-preview-side li) {
  margin-bottom: 0.25rem;
}

.markdown-editor-wrapper :deep(.editor-preview a),
.markdown-editor-wrapper :deep(.editor-preview-side a) {
  color: rgb(52 211 153);
  text-decoration: underline;
}

.markdown-editor-wrapper :deep(.editor-preview strong),
.markdown-editor-wrapper :deep(.editor-preview-side strong) {
  font-weight: 700;
  color: rgb(255 255 255);
}

.markdown-editor-wrapper :deep(.editor-preview em),
.markdown-editor-wrapper :deep(.editor-preview-side em) {
  font-style: italic;
}

.markdown-editor-wrapper :deep(.CodeMirror-scroll) {
  min-height: 320px;
}
</style>
